import { defineComponent as _defineComponent } from 'vue'
import '@/scss/material/settings/user-profile.scss';
import './ActiveSessionsPage.scss';

import { computed, onMounted, ref } from 'vue';
import moment from 'moment';

import IActiveSession from '@/@types/interfaces/IActiveSession';
import IScheduledAccountRecovery from '@/@types/interfaces/IScheduledAccountRecovery';
import apiService from '@/services/ApiService';
import authService from '@/services/AuthService';
import confirmationService from '@/services/ConfirmationService';
import notificationService from '@/services/NotificationService';
import logger from '@/other/Logger';

/**
 * SNAP-2389 — view / terminate active sessions (ASVS 7.5.2).
 *
 * Before this page the backend's `POST /user/logout` had no caller anywhere in the app, so a
 * user who suspected their account was being used elsewhere had literally nothing to click;
 * changing their password was the only lever.
 *
 * Two behaviours here are load-bearing and are pinned by `tests/unit/ActiveSessionsPage.spec.ts`:
 *
 *  - "Sign out all other devices" does NOT sign you out of the device you are holding. That
 *    is a separate, explicitly-labelled button, because being unexpectedly logged out of the
 *    page you are standing on is exactly the surprise this feature must not produce.
 *  - Ending the session you are currently using DOES sign you out, locally and immediately —
 *    the backend tells us with `wasCurrentSession` rather than us guessing from the id.
 *
 * Nothing rendered here is replayable: the API returns no `sid` and no raw IP (only a /24).
 */

export default /*#__PURE__*/_defineComponent({
  __name: 'ActiveSessionsPage',
  setup(__props, { expose }) {

const sessions = ref<IActiveSession[]>([]);
const loading = ref(true);
const loadFailed = ref(false);
const inactivityTimeoutMinutes = ref(0);
const scheduledRecovery = ref<IScheduledAccountRecovery['scheduled']>(null);
const sessionLifetimeDays = ref(0);

const MINUTES_PER_DAY = 24 * 60;

const otherSessionsCount = computed(() => sessions.value.filter((session) => !session.current).length);

const plural = (count: number, unit: string): string => (count === 1 ? `1 ${unit}` : `${count} ${unit}s`);

// SNAP-2457: whole days read as days (the 30-day default would otherwise be "720 hours"),
// anything else as hours, so 36 hours is not rounded up to "2 days".
const formatDuration = (minutes: number): string => {
  if (minutes < 60) return plural(minutes, 'minute');
  if (minutes % MINUTES_PER_DAY === 0) return plural(minutes / MINUTES_PER_DAY, 'day');
  return plural(Math.round(minutes / 60), 'hour');
};

const inactivityLabel = computed((): string =>
  inactivityTimeoutMinutes.value ? formatDuration(inactivityTimeoutMinutes.value) : 'a period of inactivity'
);

const lifetimeLabel = computed((): string => plural(sessionLifetimeDays.value, 'day'));

/** An idle limit as long as the token lifetime never fires, so the page only mentions a shorter one. */
const isIdleLimitShorter = computed(
  (): boolean =>
    inactivityTimeoutMinutes.value > 0 && inactivityTimeoutMinutes.value < sessionLifetimeDays.value * MINUTES_PER_DAY
);

const load = async () => {
  loading.value = true;
  loadFailed.value = false;

  try {
    const { data } = await apiService.getActiveSessions();
    sessions.value = data.sessions ?? [];
    inactivityTimeoutMinutes.value = data.inactivityTimeoutMinutes ?? 0;
    sessionLifetimeDays.value = data.sessionLifetimeDays ?? 0;
  } catch (error) {
    logger.error(error);
    // An evicted session is signed out, not shown a retry prompt.
    if ((error as { response?: { status?: number } })?.response?.status === 401) {
      apiService.handleResponseError(error);
      return;
    }
    // Never fall through to an empty list: "no sessions" and "we could not check" look
    // identical on screen and mean opposite things on a security page.
    loadFailed.value = true;
  } finally {
    loading.value = false;
  }
};

const formatTime = (value: string): string => (value ? moment(value).fromNow() : 'unknown');

const deviceIcon = (session: IActiveSession): string => {
  if (session.device === 'Mobile') return 'smartphone';
  if (session.device === 'Tablet') return 'tablet';
  return 'computer';
};

const revoke = async (session: IActiveSession) => {
  const message = session.current
    ? 'Sign out this device? You will be returned to the login screen.'
    : `Sign out "${session.label}"? That device will have to sign in again.`;

  try {
    await confirmationService.confirm(message, 'Sign out session', {
      confirmButtonText: 'Sign out',
      cancelButtonText: 'Cancel',
      type: 'warning',
    });
  } catch {
    return; // user cancelled
  }

  try {
    const { data } = await apiService.revokeSession(session.id);

    if (data.wasCurrentSession) {
      // The session we are holding is gone — drop the local token and leave for the login
      // screen now, rather than letting the user discover it on their next click as an
      // unexplained 401 on a blank settings page.
      authService.logoutAndRedirect();
      return;
    }

    sessions.value = sessions.value.filter((item) => item.id !== session.id);
    notificationService.success('That device has been signed out.');
  } catch (error) {
    logger.error(error);
    notificationService.error('Could not sign out that device. Please try again.');
  }
};

/** Signs out every OTHER device. This one stays signed in — and the confirmation says so. */
const logoutEverywhere = async () => {
  try {
    await confirmationService.confirm(
      'Sign out all other devices? This device will stay signed in.',
      'Sign out other devices',
      { confirmButtonText: 'Sign out others', cancelButtonText: 'Cancel', type: 'warning' }
    );
  } catch {
    return;
  }

  try {
    const { data } = await apiService.logoutAllSessions(false);
    notificationService.success(
      data.revokedCount === 1 ? '1 other device signed out.' : `${data.revokedCount} other devices signed out.`
    );
    await load();
  } catch (error) {
    logger.error(error);
    notificationService.error('Could not sign out the other devices. Please try again.');
  }
};

/** The explicit opt-in: everything, this device included. */
const logoutEverywhereIncludingCurrent = async () => {
  try {
    await confirmationService.confirm(
      'Sign out everywhere, including this device? You will be returned to the login screen.',
      'Sign out everywhere',
      { confirmButtonText: 'Sign out everywhere', cancelButtonText: 'Cancel', type: 'warning' }
    );
  } catch {
    return;
  }

  try {
    await apiService.logoutAllSessions(true);
    authService.logoutAndRedirect();
  } catch (error) {
    logger.error(error);
    notificationService.error('Could not sign out everywhere. Please try again.');
  }
};

/** SNAP-2424: a change an owner started for this account. Shown here so the member can cancel it. A failed check hides the banner and never blocks the page. */
const loadScheduledRecovery = async () => {
  try {
    const { data } = await apiService.getAccountRecoveryStatus();
    scheduledRecovery.value = data.scheduled ?? null;
  } catch (error) {
    logger.error(error);
    scheduledRecovery.value = null;
  }
};

const formatScheduledDate = (value: string): string => moment(value).format('D MMM YYYY, HH:mm');

const cancelScheduledRecovery = async () => {
  try {
    await confirmationService.confirm(
      'Cancel the sign-in email change? Your current sign-in email and password stay in place.',
      'Cancel email change',
      { confirmButtonText: 'Cancel the change', cancelButtonText: 'Keep it', type: 'warning' }
    );
  } catch {
    return; // user kept the change
  }

  try {
    await apiService.cancelOwnAccountRecovery();
    scheduledRecovery.value = null;
    notificationService.success('The sign-in email change has been cancelled.');
  } catch (error) {
    logger.error(error);
    notificationService.error('Could not cancel the change. Please try again.');
  }
};

onMounted(() => {
  load();
  loadScheduledRecovery();
});

expose({
  sessions,
  loading,
  loadFailed,
  inactivityTimeoutMinutes,
  sessionLifetimeDays,
  load,
  revoke,
  logoutEverywhere,
  logoutEverywhereIncludingCurrent,
  scheduledRecovery,
  cancelScheduledRecovery,
});

return { __sfc: true,sessions, loading, loadFailed, inactivityTimeoutMinutes, scheduledRecovery, sessionLifetimeDays, MINUTES_PER_DAY, otherSessionsCount, plural, formatDuration, inactivityLabel, lifetimeLabel, isIdleLimitShorter, load, formatTime, deviceIcon, revoke, logoutEverywhere, logoutEverywhereIncludingCurrent, loadScheduledRecovery, formatScheduledDate, cancelScheduledRecovery }
}

})